// quoteUi.jsx
// Small pieces shared by the Quote Log (list + side panel) and the record
// page (/record/:ref): light theme, status colours, tags and quote-number
// helpers.

import React from "react";

// ─── Visual theme (this page only — calculators keep the dark theme) ────────
export const C = {
  bg: "#F4F6FC",
  surface: "#FFFFFF",
  border: "#E9EBF3",
  text: "#161A2B",
  textMuted: "#6B7280",
  textFaint: "#9AA3B5",
  brand: "#3A52CC",
  brandDark: "#2B3E9E",
};

// ─── Traffic-light status colors ────────────────────────────────────────────
// Genuine traffic-light logic: red only for a real problem (Lost), amber
// for anything still in motion/waiting, green for anything moving forward or
// done, grey for a superseded row regardless of what status it froze at -
// color alone should say "fine / waiting / needs attention" at a glance.
export const STATUS_TONE = {
  Draft: "amber", Sent: "amber", Amended: "amber",
  Accepted: "green", Contracted: "green", Invoiced: "green", Complete: "green",
  "Deposit pending": "amber", "Deposit paid": "green",
  Lost: "red", Rejected: "red",
};
// Desaturated/dusty tones rather than literal stop-light colors - dusty
// rose, warm sand, sage green - so status reads calmly alongside the soft
// pastel-gradient stat cards instead of shouting over them.
export const TONE = {
  red: { bg: "#F3E6E6", text: "#A16A66", border: "#E2C7C5" },
  amber: { bg: "#F4EDE1", text: "#93763F", border: "#E4D6BC" },
  green: { bg: "#E7ECE4", text: "#6C8560", border: "#CCDAC3" },
  grey: { bg: "#EDEEF2", text: "#7D8496", border: "#D9DBE3" },
};

// For the raw numbers in a saved quote's costBreakdown (State JSON) - unlike
// the sheet's own Total/Mileage Cost columns, these are plain JS numbers,
// not pre-formatted "£x.xx" strings.
export const fmtMoney = (n) => (n == null || isNaN(n) ? null : `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
// Strips a trailing amendment letter (042a -> 042) - used to group a job's
// revisions together for the "earlier version" disclosure, regardless of how
// many amendments deep the chain goes.
export const baseNumber = (qn) => (qn || "").replace(/[a-z]+$/, "");
// Version from the quote number's letter: 027 → 1, 027a → 2, 027b → 3.
export const versionOf = (qn) => { const m = String(qn || "").match(/([a-z])$/); return m ? m[1].charCodeAt(0) - 96 + 1 : 1; };
// Test quotes (column Y) are tagged and kept out of the dashboard figures.
export const isTestQuote = q => q?.["Test Quote"] === "Yes";
export const TestTag = ({ q }) => isTestQuote(q) ? (
  <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", padding: "1px 6px", borderRadius: 4, background: "#EEF0F4", color: "#6B7280", verticalAlign: "middle" }}>TEST</span>
) : null;
export const VersionTag = ({ qn }) => versionOf(qn) > 1 ? (
  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "#F3E8FF", color: "#7C3AED", verticalAlign: "middle" }}>v{versionOf(qn)}</span>
) : null;

export function StatusBadge({ status, small, superseded }) {
  const tone = superseded ? "grey" : (STATUS_TONE[status] || "amber");
  const c = TONE[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: small ? "3px 9px" : "5px 13px",
        borderRadius: 999,
        fontSize: small ? 11 : 12,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}
